// bd-deepcontext-b4zw — owns the jobs/stats/blueprint-name load, originally
// lifted out of ScheduledJobs.tsx.
//
// bd-deepcontext-ioq0 — the 10s REST poll this hook used to run is gone.
// Refresh is now event-driven: the backend already streams a
// `jobs_update` event over SSE whenever any job's run-state changes
// (ScheduledJobController.streamAllJobsUpdates), so this just refetches on
// receipt instead of patching state from the event payload (v1 scope). The
// page keeps the error/success banners, so failures come back through `onError`.
import { useCallback, useEffect, useRef, useState } from "react";
import { promptApi } from "../../services/api";
import { useEventStream } from "../../hooks/useEventStream";
import { scheduledJobsApi } from "./api";
import { sortJobsByActivity } from "./jobList";
import { EMPTY_STATS, type ScheduledJob, type ScheduledJobStats } from "./types";

// Matches the base URL construction runsStream.ts already uses for the
// per-job runs SSE endpoint — the fetch-based stream needs an absolute URL in
// dev (frontend and backend run on different ports).
const STREAM_URL = `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/api/scheduled-jobs/stream`;

export interface ScheduledJobsData {
  jobs: ScheduledJob[];
  stats: ScheduledJobStats;
  blueprintNames: Record<string, string>;
  loading: boolean;
  /** Mutation handlers share the page's spinner, so they drive this directly. */
  setLoading: (loading: boolean) => void;
  reload: () => Promise<void>;
}

export function useScheduledJobsData({
  onError,
}: {
  onError: (message: string) => void;
}): ScheduledJobsData {
  const [jobs, setJobs] = useState<ScheduledJob[]>([]);
  // bd-wxgk9 — blueprint names for the clickable name-pills. The jobs payload
  // carries only promptIds, so resolve id -> name client-side. Keyed by promptId.
  const [blueprintNames, setBlueprintNames] = useState<Record<string, string>>(
    {},
  );
  const [stats, setStats] = useState<ScheduledJobStats>(EMPTY_STATS);
  const [loading, setLoading] = useState(false);

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      // bd-5gpue — eager-load blueprint names in the SAME phase as the jobs via
      // a single bulk fetch (promptApi.getAll, ?pageSize=1000) instead of the
      // old N+1 getById fan-out. Building the id -> name map here means the
      // name-pills render with their names on first paint (no "Loading…").
      const [jobsData, statsData, allPrompts] = await Promise.all([
        scheduledJobsApi.getJobs(),
        scheduledJobsApi.getStats(),
        promptApi
          .getAll()
          .catch(() => [] as Awaited<ReturnType<typeof promptApi.getAll>>),
      ]);

      // Merge (don't replace) so the 10s auto-refresh never blanks resolved
      // names. Any id absent from the bulk list falls back to the id in the pill.
      if (allPrompts.length > 0) {
        setBlueprintNames((prev) => {
          const next = { ...prev };
          for (const p of allPrompts) next[p.id] = p.name;
          return next;
        });
      }

      setJobs(sortJobsByActivity(jobsData));
      setStats(statsData);
    } catch (err: any) {
      console.error("Failed to load data:", err);
      if (err.response?.status === 403) {
        onErrorRef.current("Admin access required to view scheduled jobs");
      } else {
        onErrorRef.current("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEventStream(STREAM_URL, { jobs_update: reload });

  return { jobs, stats, blueprintNames, loading, setLoading, reload };
}
